import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Globe, MapPin, HelpCircle, UserX, Mail } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Modal types for account settings
 */
export const SETTINGS_TYPES = {
  MY_INFO: 'my-info',
  LANGUAGE: 'language',
  LOCATION: 'location',
  FAQ: 'faq',
  BLOCKED_USERS: 'blocked-users',
  NOTIFICATION_PREFERENCES: 'notification-preferences',
};

/**
 * Get modal config based on type
 */
const getModalConfig = (type, t) => {
  const configs = {
    [SETTINGS_TYPES.MY_INFO]: {
      title: t('my_info', 'My Info'),
      icon: User,
    },
    [SETTINGS_TYPES.LANGUAGE]: {
      title: t('select_language', 'Select Language'),
      icon: Globe,
    },
    [SETTINGS_TYPES.LOCATION]: {
      title: t('change_location', 'Change Location'),
      icon: MapPin,
    },
    [SETTINGS_TYPES.FAQ]: {
      title: t('faq', 'Frequently Asked Questions'),
      icon: HelpCircle,
    },
    [SETTINGS_TYPES.BLOCKED_USERS]: {
      title: t('blocked_users', 'Blocked Users'),
      icon: UserX,
    },
    [SETTINGS_TYPES.NOTIFICATION_PREFERENCES]: {
      title: t('email_notifications_section_title', 'Email Notifications'),
      icon: Mail,
    },
  };
  return configs[type] || { title: t('settings', 'Settings'), icon: User };
};

const getModalSizeClass = (type) => {
  if (type === SETTINGS_TYPES.FAQ) {
    return 'max-w-2xl';
  }
  if (
    type === SETTINGS_TYPES.BLOCKED_USERS ||
    type === SETTINGS_TYPES.NOTIFICATION_PREFERENCES
  ) {
    return 'max-w-lg';
  }
  return 'max-w-md';
};

/**
 * AccountSettingsModal - Modal for account settings sections
 * Matches the reference project styling
 */
const AccountSettingsModal = ({ isOpen, onClose, type, children }) => {
  const { t } = useTranslation();
  const config = getModalConfig(type, t);
  const sizeClass = getModalSizeClass(type);

  // Handle close
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className={`bg-white rounded-2xl ${sizeClass} w-full max-h-[85vh] overflow-hidden flex flex-col`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border flex-shrink-0">
              <h3 className="text-base sm:text-xl font-bold text-foreground pr-2 line-clamp-1">{config.title}</h3>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleClose} 
                className="min-w-[36px] min-h-[36px] sm:min-w-[40px] sm:min-h-[40px] flex-shrink-0"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {children}
            </div>

            {/* Footer with Close Button */}
            <div className="p-4 sm:p-6 border-t border-border flex-shrink-0">
              <Button 
                onClick={handleClose} 
                className="w-full min-h-[44px] bg-primary hover:bg-primary/90 text-sm sm:text-base"
              >
                {t('close', 'Close')}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AccountSettingsModal;
